import React from 'react';

import {Loading} from 'Components/Partials';
import {FormHandler, FileUpload, Input, Select, Textarea, Toggle, WYSIWYG, DatePicker} from 'Components/Form';
import {Alert} from 'Components/Partials';
import {PrimaryButton} from 'Components/Button';

import ElementApi from 'Services/Api/Cms/Elements/Element';
import RecordApi from 'Services/Api/Cms/Elements/Record';
import TagApi from 'Services/Api/Cms/Elements/Tag';
import GroupsApi from 'Services/Api/Groups/Groups';

import {translation} from 'Services/TranslationHelpers';
import {translationFromJson} from 'Services/TranslationHelpers2';
import {getRecordValue} from 'Services/CmsHelpers';
import {User} from 'Services';

class CreateCmsElementRecord extends React.Component {
    /**
     * @var success
     * @type {string}
     */
    success = this.props.element_record_id && !this.props.clone ? 
        translation('misc', 'item_updated') : 
        translation('misc', 'item_created');

    /**
     * @var state
     */
    state = {
        working: true,
        elements: null,
        selected_element_id: null,
        tags: null,
        errorMessage: null
    };

    /**
     * @method componentDidMount
     */
    componentDidMount = async () => {
        const {showElementsDropdown, element_id, element_record_id} = this.props;

        if (!showElementsDropdown && !element_id) {
            console.error('Must pre-select an element if not showing the elements dropdown.');
            return;
        }

        if (showElementsDropdown && element_record_id) {
            console.error('Can not show elements dropdown and be updating a record.');
            return;
        }

        await this.getElements();

        if (element_id) {
            await this.setElement(element_id);
        }

        this.setState({ working: false });
    }

    /**
     * @method getElements
     * @return null
     */
    getElements = async () => {
        const {element_id, include_restricted = false} = this.props;

        const response = await ElementApi.get(null, {
            include_restricted
        });

        let elements = response.data.data;

        if (!User.data.is_admin) {
            elements = elements.filter(e => e.display_form_on_frontend);
        }

        this.setState({
            elements
        });
    }

    /**
     * @method setElement
     * @param {string} element_id
     * @return null
     */
    setElement =  async (element_id) => {
        const {element_record_id, setTitleCallback, clone = false} = this.props;
        const {elements} = this.state;

        let element = elements.filter(e => e.id === element_id)[0];

        if (!element) {
            this.setState({
                errorMessage: 'This form has been disabled for non-admin users.'
            });
            return;
        }

        this.setState({
            selected_element_id: element_id
        }, async () => {
            await this.getTags();

            let element_label = translationFromJson(element.label);

            if (element_record_id) {
                await this.getRecord(element_record_id);
            } else {
                await this.setEmptyRecord();
            }

            if (setTitleCallback) {
                setTitleCallback(translation(
                    'misc',
                    element_record_id && !clone ? 'update_item' : 'create_item',
                    {
                        ITEM: element_label
                    }
                ));
            }
        })
    }

    /**
     * @method getTags
     * @return null
     */
    getTags = async () => {
        const response = await TagApi.get(null, {
            cms_element_id: this.state.selected_element_id
        });

        this.setState({
            tags: response.data.tags,
        });
    }

    /**
     * @method getRecord
     * @param {string} element_record_id
     * @return null
     */
    getRecord = async (element_record_id) => {
        const {setForm} = this.props;
        const {elements, selected_element_id} = this.state;

        const response = await RecordApi.get(element_record_id);

        let record = response.data.data;

        let form = {};

        let element = elements.filter(o => o.id === selected_element_id)[0];

        {Object.entries(element.fields).map((field, j) => {
            form[field[1].identifier] = getRecordValue(record, field[1].identifier);
        })}

        form.active = record.active;
        form.supported_languages = record.supported_languages;
        form.group_ids = record.group_ids;
        form.allow_likes = record.allow_likes;
        form.allow_comments = record.allow_comments;
        form.tags = record.tags.map((o) => (o.tag));

        setForm(form);
    }

    setEmptyRecord = async () => {
        const {setInitialValues} = this.props;
        const {elements, selected_element_id} = this.state;

        let form = {};

        let element = elements.filter(o => o.id === selected_element_id)[0];

        {Object.entries(element.fields).map((field, j) => {
            let value = null;

            if (field[1].type === 'boolean') {
                value = false;
            } else if (field[1].type === 'date' || field[1].type === 'time') {
                value = new Date();
            }

            form[field[1].identifier] = value;
        })}

        form.active = true;
        form.supported_languages = Object.keys(window.base.languages);
        form.group_ids = null;

        if (element.allow_likes) {
            form.allow_likes = true;
        } else {
            form.allow_likes = false;
        }

        if (element.allow_comments) {
            form.allow_comments = true;
        } else {
            form.allow_comments = false;
        }

        form.tags = null;

        setInitialValues(form);
    }

    /**
     * @method createRecord
     * @param {object} form
     * @return {Promise<*>}
     */
    createRecord = (form) => {
        const {elements, selected_element_id} = this.state;
        const {element_record_id, clone = false} = this.props;

        let element = elements.filter(o => o.id === selected_element_id)[0];

        let form2 = {
            ...form,
            cms_element_id: selected_element_id
        };

        if (element_record_id && !clone) {
            return RecordApi.updateRecord(element_record_id, form2, element.fields);
        } else {
            return RecordApi.storeRecord(form2, element.fields);
        }
    };

    /**
     * @method render
     * @return {JSX.Element}
     */
    render() {
        const {working, elements, selected_element_id, errorMessage} = this.state;
        const {showElementsDropdown} = this.props;

        let element_options = elements ?
            elements.map((element, key) => {
                return {
                    label: translationFromJson(element.label),
                    value: element.id
                };
            }) :
            null;

        if (errorMessage) {
            return (
                <div className="text-center">
                    {errorMessage}
                </div>
            );
        }

        return (
            <div>
                {working && (<Loading />)}

                {!working && showElementsDropdown &&
                    <Select
                        containerClassName="mb-4"
                        label={translation('cms', 'item_type')}
                        value={selected_element_id}
                        onChange={(v) => this.setElement(v)}
                        options={element_options}
                    />
                }

                {!working && selected_element_id && this.renderForm()}
            </div>
        );
    }

    /**
     * @method renderForm
     * @return {JSX.Element}
     */
    renderForm = () => {
        const {handleInput, handleSubmit, form, working, alert, element_record_id, clone = false, onClose} = this.props;
        const {elements, selected_element_id} = this.state;

        let element = elements.filter(o => o.id === selected_element_id)[0];

        return (
            <form onSubmit={(e) => handleSubmit(
                e,
                this.createRecord,
                onClose ?? this.success,
                (element_record_id && !clone ? false : true)
            )}>
                {Object.entries(element.fields).map((field, j) => {
                    return this.renderInput(field[1], j);
                })}

                {User.data.is_admin && this.renderDefaultFields()}

                {alert && <Alert {...alert} />}

                <div className={`w-full mt-8 flex ${User.data.is_admin ? 'flex-row-reverse' : 'justify-center'}`}>
                    <PrimaryButton
                        working={working}
                        text={element_record_id && !clone ? translation('misc', 'update') : translation('misc', 'create')}
                    />
                </div>
            </form>
        );
    };

    /**
     * @method renderDefaultFields
     * @return {JSX.Element}
     */
    renderDefaultFields = () => {
        const {handleInput, getFieldError, form} = this.props;
        const {elements, selected_element_id, tags} = this.state;

        let element = elements.filter(o => o.id === selected_element_id)[0];

        let languages = Object.entries(window.base.languages).map((value, key) => {
            return {
                label: value[1],
                value: value[0]
            };
        });

        let options = tags ? tags.map((value, key) => {
            return {
                label: value,
                value: value
            };
        }) : null;

        return (
            <>
                {element.filter_languages && languages.length > 1 &&
                    <div>
                        <Select
                            containerClassName="mb-4"
                            label="Supported Languages"
                            value={form.supported_languages}
                            error={getFieldError('supported_languages')}
                            onChange={(v) => handleInput('supported_languages', v)}
                            options={languages}
                            isMulti
                        />
                    </div>
                }

                {element.filter_groups &&
                    <div>
                        <Select
                            containerClassName="mb-4"
                            label="Groups"
                            value={form.group_ids}
                            error={getFieldError('group_ids')}
                            onChange={(v) => handleInput('group_ids', v)}
                            isAsync
                            isMulti
                            searchCallback={(data) => GroupsApi.get(null, {
                                ...data,
                                hasNoTeam: true
                            })}
                            searchLabelKey="name"
                            instructions="Leave empty to open the record to all users"
                        />
                    </div>
                }

                {element.allow_tags &&
                    <Select
                        containerClassName="mb-4"
                        label="Tags"
                        value={form.tags}
                        error={getFieldError('tags')}
                        onChange={v => handleInput('tags', v)}
                        options={options}
                        isMulti
                        isCreatable
                    />
                }

                {element.allow_likes &&
                    <Toggle
                        containerClassName="mb-4"
                        label="Allow Likes"
                        value={form.allow_likes}
                        error={getFieldError('allow_likes')}
                        onChange={v => handleInput('allow_likes', v)}
                    />
                }

                {element.allow_comments &&
                    <Toggle
                        containerClassName="mb-4"
                        label="Allow Comments"
                        value={form.allow_comments}
                        error={getFieldError('allow_comments')}
                        onChange={v => handleInput('allow_comments', v)}
                    />
                }

                <Toggle
                    containerClassName="mb-4"
                    label="Active"
                    value={form.active}
                    error={getFieldError('active')}
                    onChange={v => handleInput('active', v)}
                />
            </>
        );
    }

    /**
     * @method renderInput
     * @param {object} field
     * @param {string} key
     * @return {JSX.Element}
     */
    renderInput = (field, key) => {
        if (this.customRules(field)) {
            let label = translationFromJson(field.label);

            if (!this['renderInput_' + field.type]) {
                console.error('Invalid field type : ' + field.type);
            }

            return this['renderInput_' + field.type](field, key, label);
        }
    };

    /**
     * @method customRules
     * @param {object} field
     * @return {boolean}
     */
    customRules = (field) => {
        // Add any custom site rules here, e.g. only show one field depending on another value if element.identifier='x'.

        const {form} = this.props;
        const {elements, selected_element_id} = this.state;

        let element = elements.filter(o => o.id === selected_element_id)[0];

        if (element.identifier === 'celebration-wall') {
            if (field.identifier === 'file') {
                let content_type_field_id = element.fields.filter(o => o.identifier === 'content_type')[0].identifier;

                if (form[content_type_field_id] !== 'image' && form[content_type_field_id] !== 'video') {
                    return false;
                }
            }
        }

        return true;
    }

    /**
     * @method renderInput_text
     * @param {object} field
     * @param {string} key
     * @param {string} label
     * @return {JSX.Element}
     */
    renderInput_text = (field, key, label) => {
        const {handleInput, getFieldError, form} = this.props;

        return (
            <Input
                containerClassName="mb-4"
                label={label}
                instructions={field.instructions}
                type="text"
                value={form[field.identifier]}
                error={getFieldError(field.identifier)}
                onChange={v => handleInput(field.identifier, v)}
                key={key}
            />
        );
    };

    /**
     * @method renderInput_textarea
     * @param {object} field
     * @param {string} key
     * @param {string} label
     * @return {JSX.Element}
     */
    renderInput_textarea = (field, key, label) => {
        const {handleInput, getFieldError, form} = this.props;

        return (
            <Textarea
                containerClassName="mb-4"
                label={label}
                instructions={field.instructions}
                value={form[field.identifier]}
                error={getFieldError(field.identifier)}
                onChange={v => handleInput(field.identifier, v)}
                key={key}
            />
        );
    };

    /**
     * @method renderInput_file
     * @param {object} field
     * @param {string} key
     * @param {string} label
     * @return {JSX.Element}
     */
    renderInput_file = (field, key, label) => {
        const {handleInput, getFieldError, form} = this.props;

        return (
            <FileUpload
                containerClassName="mb-4 flex flex-col justify-center items-center gap-4"
                label={label}
                instructions={field.instructions}
                value={form[field.identifier]}
                error={getFieldError(field.identifier)}
                onChange={v => handleInput(field.identifier, v)}
                accept={field.file_accept}
                placeholder={form[field.identifier] ? 
                    translation('misc', 'update_file') : 
                    translation('misc', 'select_file')}
                allowDownloadExisting={true}
                key={key}
            />
        );
    };

    /**
     * @method renderInput_boolean
     * @param {object} field
     * @param {string} key
     * @param {string} label
     * @return {JSX.Element}
     */
    renderInput_boolean = (field, key, label) => {
        const {handleInput, getFieldError, form} = this.props;

        return (
            <Toggle
                containerClassName="mb-4"
                label={label}
                instructions={field.instructions}
                value={form[field.identifier]}
                error={getFieldError(field.identifier)}
                onChange={v => handleInput(field.identifier, v)}
                key={key}
            />
        );
    };

    /**
     * @method renderInput_multiple_choice_single_value
     * @param {object} field
     * @param {string} key
     * @param {string} label
     * @return {JSX.Element}
     */
    renderInput_multiple_choice_single_value = (field, key, label) => {
        const {handleInput, getFieldError, form} = this.props;

        let options = field.multiple_choice_values.map((value, key) => {
            let translation_value = translation('misc', value, null, false);

            return {
                label: translation_value && translation_value !== '' ? translation_value : value,
                value: value
            };
        });

        return (
            <Select
                containerClassName="mb-4"
                label={label}
                instructions={field.instructions}
                value={form[field.identifier]}
                error={getFieldError(field.identifier)}
                onChange={v => handleInput(field.identifier, v)}
                options={options}
                key={key}
            />
        );
    };

    /**
     * @method renderInput_wysiwyg
     * @param {object} field
     * @param {string} key
     * @param {string} label
     * @return {JSX.Element}
     */
    renderInput_wysiwyg = (field, key, label) => {
        const {handleInput, getFieldError, form} = this.props;

        return (
            <WYSIWYG
                containerClassName="mb-4"
                label={label}
                instructions={field.instructions}
                value={form[field.identifier]}
                error={getFieldError(field.identifier)}
                onChange={value => handleInput(field.identifier, value)}
                key={key}
            />
        );
    }

    /**
     * @method renderInput_date
     * @param {object} field
     * @param {string} key
     * @param {string} label
     * @return {JSX.Element}
     */
    renderInput_date = (field, key, label) => {
        const {handleInput, getFieldError, form} = this.props;

        return (
            <DatePicker
                containerClassName="mb-4"
                label={label}
                instructions={field.instructions}
                selected={form[field.identifier]}
                error={getFieldError(field.identifier)}
                onChange={date => handleInput(field.identifier, date)}
                key={key}
            />
        );
    }

    /**
     * @method renderInput_time
     * @param {object} field
     * @param {string} key
     * @param {string} label
     * @return {JSX.Element}
     */
    renderInput_time = (field, key, label) => {
        const {handleInput, getFieldError, form} = this.props;

        return (
            <DatePicker
                containerClassName="mb-4"
                label={label}
                instructions={field.instructions}
                selected={form[field.identifier]}
                error={getFieldError(field.identifier)}
                onChange={time => handleInput(field.identifier, time)}
                showTimeSelect={true}
                showTimeSelectOnly={true}
                timeIntervals={15}
                key={key}
            />
        );
    }

    /**
     * @method renderInput_cashier_meta_data
     * @param {object} field
     * @param {string} key
     * @param {string} label
     * @return {JSX.Element}
     */
    renderInput_cashier_meta_data = (field, key, label) => {
        const {handleInput, getFieldError, form} = this.props;

        return (
            <CashierMetaData
                containerClassName="flex flex-col gap-4 mb-4"
                label={label}
                instructions={field.instructions}
                value={form[field.identifier]}
                getFieldError={(v) => getFieldError(field.identifier + '.' +  v)}
                onChange={time => handleInput(field.identifier, time)}
                key={key}
            />
        );
    }
}

export default FormHandler(CreateCmsElementRecord);
